import { z } from 'zod'

export const CRM_WORK_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM work read alias. Domain validation remains in the handlers. */
export const crmWorkReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  cursor: optionalQueryString,
  status: optionalQueryString,
  dueSoonHours: optionalQueryString,
  due_soon_hours: optionalQueryString,
  leadOwnerUserId: optionalQueryString,
  lead_owner_user_id: optionalQueryString,
  q: optionalQueryString,
  scope: optionalQueryString,
  provider: optionalQueryString,
  source: optionalQueryString,
  subjectType: optionalQueryString,
}).passthrough()

const closedReadSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.boolean(),
}).passthrough()

const crm360ReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmTaskStatusSchema = z.enum(['OPEN', 'DONE'])

export const crmTaskSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  leadId: z.string(),
  createdByUserId: z.string(),
  assignedToUserId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  dueAt: z.string().nullable(),
  status: crmTaskStatusSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmTaskListSummarySchema = crmTaskSummarySchema.extend({
  leadCompanyName: z.string(),
  leadFirstName: z.string().nullable(),
  leadLastName: z.string().nullable(),
}).passthrough()

export const crmTaskListResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  tasks: z.array(crmTaskListSummarySchema),
  sideEffects: closedReadSideEffectsSchema,
}).passthrough()

export const crmSearchScopeSchema = z.enum(['all', 'person', 'org', 'client'])

export const crmSearchResultSchema = z.object({
  entityType: z.enum(['person', 'org', 'client']),
  goldenId: z.string(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  rank: z.number().finite(),
  matchedVia: z.string(),
  engagementBand: z.string().nullable(),
  membershipStatus: z.string().nullable(),
  tier: z.string().nullable(),
  nextBestAction: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  hasUnresolvedCandidates: z.boolean(),
  href: z.string().nullable(),
}).passthrough()

export const crmSearchResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  search: z.object({
    query: z.object({
      q: z.string(),
      scope: crmSearchScopeSchema,
      normalized: z.object({
        email: z.string().nullable(),
        phone: z.string().nullable(),
      }).passthrough(),
    }).passthrough(),
    results: z.array(crmSearchResultSchema),
    nextCursor: z.string().nullable(),
  }).passthrough(),
  sideEffects: crm360ReadSideEffectsSchema,
}).passthrough()

export const crmAssignableCrmRoleSchema = z.enum(['MANAGER', 'SALES', 'SUPPORT'])

export const crmAssignableUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  crmRole: crmAssignableCrmRoleSchema,
}).passthrough()

export const crmAssignableUsersResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  users: z.array(crmAssignableUserSchema),
}).passthrough()

export const crmEnrichmentSubjectTypeSchema = z.enum(['crm_message', 'crm_transcript', 'crm_company', 'crm_contact'])
export const crmEnrichmentSourceSchema = z.enum(['llm_extraction', 'provider_enrichment', 'manual'])
export const crmEnrichmentStatusSchema = z.enum(['proposed', 'approved', 'rejected', 'applied'])

const crmEnrichmentProposedChangesSchema = z.object({
  company: z.object({
    segment: z.string().optional(),
    tier: z.string().optional(),
    notes: z.string().optional(),
  }).passthrough().optional(),
  contact: z.object({
    title: z.string().optional(),
  }).passthrough().optional(),
  tasks: z.array(z.object({
    title: z.string(),
    description: z.string().optional(),
    dueAt: z.string().nullable().optional(),
  }).passthrough()).optional(),
}).passthrough()

export const crmEnrichmentSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  subjectType: crmEnrichmentSubjectTypeSchema,
  subjectId: z.string(),
  source: crmEnrichmentSourceSchema,
  model: z.string().nullable(),
  proposedChanges: crmEnrichmentProposedChangesSchema,
  status: crmEnrichmentStatusSchema,
  createdByUserId: z.string().nullable(),
  reviewedByUserId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  appliedResult: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmEnrichmentQueueResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  proposals: z.array(crmEnrichmentSummarySchema),
  nextCursor: z.string().nullable(),
  sideEffects: closedReadSideEffectsSchema,
}).passthrough()

export const crmProviderConnectionProviderSchema = z.enum([
  'zoom',
  'fireflies',
  'other',
  'email_inbox',
  'imap',
  'gmail',
  'ms_graph',
])
export const crmProviderConnectionStatusSchema = z.enum(['connected', 'disconnected', 'unprovisioned'])

export const crmProviderConnectionSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  provider: crmProviderConnectionProviderSchema,
  accountRef: z.string(),
  displayName: z.string().nullable(),
  credentialConfigured: z.boolean(),
  status: crmProviderConnectionStatusSchema,
  lastSyncAt: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  pollCursorPresent: z.boolean(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmProviderConnectionListResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  connections: z.array(crmProviderConnectionSummarySchema),
  sideEffects: closedReadSideEffectsSchema,
}).passthrough()

export const CRM_WORK_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_360_DISABLED',
] as const

export const crmWorkReadErrorEnvelopeSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(CRM_WORK_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmWorkReadQuery = z.infer<typeof crmWorkReadQuerySchema>
export type CrmTaskStatus = z.infer<typeof crmTaskStatusSchema>
export type CrmTaskSummary = z.infer<typeof crmTaskSummarySchema>
export type CrmTaskListSummary = z.infer<typeof crmTaskListSummarySchema>
export type CrmTaskListResponse = z.infer<typeof crmTaskListResponseSchema>
export type CrmSearchScope = z.infer<typeof crmSearchScopeSchema>
export type CrmSearchResult = z.infer<typeof crmSearchResultSchema>
export type CrmSearchResponse = z.infer<typeof crmSearchResponseSchema>
export type CrmAssignableCrmRole = z.infer<typeof crmAssignableCrmRoleSchema>
export type CrmAssignableUser = z.infer<typeof crmAssignableUserSchema>
export type CrmAssignableUsersResponse = z.infer<typeof crmAssignableUsersResponseSchema>
export type CrmEnrichmentSubjectType = z.infer<typeof crmEnrichmentSubjectTypeSchema>
export type CrmEnrichmentSource = z.infer<typeof crmEnrichmentSourceSchema>
export type CrmEnrichmentStatus = z.infer<typeof crmEnrichmentStatusSchema>
export type CrmEnrichmentSummary = z.infer<typeof crmEnrichmentSummarySchema>
export type CrmEnrichmentQueueResponse = z.infer<typeof crmEnrichmentQueueResponseSchema>
export type CrmProviderConnectionProvider = z.infer<typeof crmProviderConnectionProviderSchema>
export type CrmProviderConnectionStatus = z.infer<typeof crmProviderConnectionStatusSchema>
export type CrmProviderConnectionSummary = z.infer<typeof crmProviderConnectionSummarySchema>
export type CrmProviderConnectionListResponse = z.infer<typeof crmProviderConnectionListResponseSchema>
export type CrmWorkReadErrorCode = (typeof CRM_WORK_READ_ERROR_CODES)[number]
export type CrmWorkReadErrorEnvelope = z.infer<typeof crmWorkReadErrorEnvelopeSchema>
